// ================================
// CUSTOMER CARD ACTIVATION
// First login for a member handed a pre-printed card. No PIN was ever sent
// to them — they scan their card's QR (or type the M.I.D. printed on it)
// and choose their own PIN right here.
// ================================
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Html5Qrcode } from 'html5-qrcode';

export default function CustomerActivate() {
  const [searchParams]        = useSearchParams();
  const [mid, setMid]         = useState(searchParams.get('mid') || '');
  const [pin, setPin]         = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [error, setError]     = useState('');
  const [loading, setLoading] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const scannerRef = useRef(null);
  const { activateCustomer }  = useAuth();
  const navigate               = useNavigate();

  useEffect(() => {
    if (!showScanner) return;
    const scanner = new Html5Qrcode('activate-qr-reader');
    scannerRef.current = scanner;
    let stopped = false;
    // start() can fail (permission denied, no camera) — stop() on a
    // scanner that never actually got running throws synchronously, not
    // just a rejected promise, so cleanup has to know whether it's safe
    // to call at all.
    let started = false;

    scanner.start(
      { facingMode: 'environment' },
      { fps: 10, qrbox: 220 },
      (decodedText) => {
        if (stopped) return;
        stopped = true;
        // Cards encode a real URL (https://.../c/<mid>) now — pull the mid
        // out of the path instead of a bare "WWMID:" prefix.
        const match = /\/c\/([^/?#]+)/.exec(decodedText.trim());
        setMid(match ? match[1] : decodedText.trim());
        setShowScanner(false);
      },
      () => {}
    ).then(() => { started = true; })
     .catch(() => alert('Could not access the camera. Check camera permissions and try again.'));

    return () => {
      stopped = true;
      if (!started) return;
      try { scanner.stop().then(() => scanner.clear()).catch(() => {}); } catch (e) {}
    };
  }, [showScanner]);

  async function handleActivate(e) {
    e.preventDefault();
    setError('');
    if (pin !== confirmPin) { setError('PINs do not match'); return; }
    if (pin.length < 4) { setError('PIN must be at least 4 digits'); return; }

    setLoading(true);
    try {
      await activateCustomer(mid, pin);
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.error || 'Could not activate this card');
    } finally { setLoading(false); }
  }

  return (
    <div className="app-shell" style={{ padding:'28px' }}>
      <button className="back-btn" onClick={() => navigate('/login')}>← Back</button>
      <h2 style={{ fontFamily:'Bebas Neue, sans-serif', fontSize:30, letterSpacing:2, marginBottom:4 }}>Activate Your Card</h2>
      <p style={{ fontSize:13, color:'#3a5a8a', marginBottom:28 }}>
        Scan your member card or enter your M.I.D., then choose a PIN — this is your first time signing in.
      </p>

      <button type="button" onClick={() => setShowScanner(true)} style={{
        width:'100%', padding:'12px', marginBottom:20, borderRadius:'var(--radius-lg)',
        border:'1px solid var(--border)', background:'var(--bg-card)', color:'var(--accent-blue)',
        fontSize:14, cursor:'pointer'
      }}>
        📷 Scan card QR code
      </button>

      {showScanner && (
        <div style={{ marginBottom:20 }}>
          <div id="activate-qr-reader" style={{ borderRadius:'var(--radius-lg)', overflow:'hidden' }} />
          <button type="button" onClick={() => setShowScanner(false)} style={{
            marginTop:8, background:'none', border:'none', color:'#3a5a8a', fontSize:12, cursor:'pointer'
          }}>Cancel scan</button>
        </div>
      )}

      <form onSubmit={handleActivate}>
        <div className="field">
          <label>Member ID (M.I.D.)</label>
          <input type="text" placeholder="12345" maxLength={5} value={mid} onChange={e => setMid(e.target.value)} required />
        </div>
        <div className="field">
          <label>Choose a PIN</label>
          <input type="password" placeholder="••••" maxLength={6} value={pin} onChange={e => setPin(e.target.value)} required />
        </div>
        <div className="field">
          <label>Confirm PIN</label>
          <input type="password" placeholder="••••" maxLength={6} value={confirmPin} onChange={e => setConfirmPin(e.target.value)} required />
        </div>
        {error && <p style={{ color:'#c0101a', fontSize:13, marginBottom:12 }}>{error}</p>}
        <div style={{ marginTop:24 }}>
          <button className="btn-primary" type="submit" disabled={loading}>
            {loading ? 'Activating...' : 'Activate & Sign In'}
          </button>
          <p style={{ textAlign:'center', fontSize:12, color:'#3a5a8a', marginTop:14 }}>
            Already activated? <span style={{ color:'#1e6bff', cursor:'pointer' }} onClick={() => navigate('/login')}>Sign in</span>
          </p>
        </div>
      </form>
    </div>
  );
}
